import React, { useRef } from 'react';
import { motion, MotionValue, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ventureQuote } from '../../../data/ventureStory';
import { useLanguage } from '../../../contexts/LanguageContext';

// A full-screen frame held in place while the line writes itself, word by word
export function CinemaQuote() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  // Starts reacting as the frame enters view, not once it is pinned
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start 0.6', 'end end']
  });
  const scale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1.1, 1]);
  const shade = useTransform(scrollYProgress, [0, 0.4, 1], [0.25, 0.5, 0.65]);
  const {
    tr
  } = useLanguage();
  // Translated as a whole line, then split for the word-by-word reveal
  const quote = tr(ventureQuote.quote);
  const words = quote.split(' ');
  return <section ref={ref} aria-label={quote} data-i18n-skip className="relative h-[150vh]" data-id="element-1232" data-mp="3sg4h8661xxnq">
      <div className="sticky top-0 flex h-screen items-center overflow-hidden" data-id="element-1233" data-mp="10yvu3ge5cn1y">
        <motion.img src={ventureQuote.image} alt={ventureQuote.alt} style={{
        scale
      }} className="absolute inset-0 h-full w-full object-cover" data-id="element-1234" data-mp="wxiq7bum6txo" />
        <motion.div style={{
        opacity: shade
      }} className="absolute inset-0 bg-ink" aria-hidden="true" data-id="element-1235" data-mp="1apyz2ta75m6i" />
        <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.06] motion-reduce:hidden" aria-hidden="true" data-id="element-1236" data-mp="15vckkh8tjdi7" />
        <p className="relative mx-auto max-w-[1200px] px-6 text-center font-editorial text-[56px] leading-[60px] tracking-[-0.02em] md:px-16 md:text-[120px] md:leading-[118px]" aria-hidden="true" data-id="element-1237" data-mp="1fnibror8xqwc">
          {words.map((w, i) => <React.Fragment key={`${w}-${i}`} data-id="element-1238" data-mp="1oo6psjgn3r98" data-mpi={i}>
              <Word word={w} progress={scrollYProgress} range={[0.05 + i / words.length * 0.45, 0.05 + (i + 1) / words.length * 0.45]} data-id="element-1239" data-mp="3ve4r0wuqnyog" />
              {/* On phones, each sentence takes its own line */}
              {/[.。]$/.test(w) && i < words.length - 1 && <br className="md:hidden" data-id="element-1240" data-mp="1udgf3zkc89e1" />}
            </React.Fragment>)}
        </p>
      </div>
    </section>;
}
type WordProps = {
  word: string;
  progress: MotionValue<number>;
  range: [number, number];
};
function Word({
  word,
  progress,
  range
}: WordProps) {
  const opacity = useTransform(progress, range, [0.12, 1]);
  const y = useTransform(progress, range, [14, 0]);
  return <motion.span style={{
    opacity,
    y
  }} className="inline-block" data-id="element-1241" data-mp="2d9yff5d95hlw">
      {word}&nbsp;
    </motion.span>;
}